import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../../../theming/useTheme';
import { RegularHeaderLink } from './RegularHeaderLink';
import { TypographySizes } from '../../../../../typography/semanticTags/semanticTags.utils';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';

export const componentConfigThemeKey = 'sportradar';

export interface SportradarThemeProps {
  titleColor: string;
  backgroundColor: string;
  hoverColor: string;
  linkColor: string;
}

const NAV_CONFIG = {
  GAME: {
    text: 'Game',
    href: '',
  },
  BOX_SCORE: {
    text: 'Box Score',
    href: 'boxscore',
  },
  PLAY_BY_PLAY: {
    text: 'Play by Play',
    href: 'play-by-play',
  },
  BETTING: {
    text: 'Betting',
    href: 'betting',
  },
  TICKETS: {
    text: 'Tickets',
    href: 'https://www.sitickets.com/',
  },
  HOME: {
    text: 'Home',
    href: '',
  },
  SCHEDULE: {
    text: 'Schedule',
    href: 'schedule',
  },
  ROSTER: {
    text: 'Roster',
    href: 'roster',
  },
  STATS: {
    text: 'Stats',
    href: 'stats',
  },
  TEAM_TICKETS: {
    text: 'Tickets',
    href: 'https://www.sitickets.com/performer',
  },
};

const PATHS = [
  NAV_CONFIG.BOX_SCORE.href,
  NAV_CONFIG.PLAY_BY_PLAY.href,
  NAV_CONFIG.BETTING.href,
  NAV_CONFIG.SCHEDULE.href,
  NAV_CONFIG.ROSTER.href,
  NAV_CONFIG.STATS.href,
];

// hard code path for pages
const PAGE_LINKS: Record<string, any> = {
  past: [NAV_CONFIG.GAME, NAV_CONFIG.BOX_SCORE, NAV_CONFIG.PLAY_BY_PLAY],
  future: [NAV_CONFIG.GAME, NAV_CONFIG.BETTING, NAV_CONFIG.TICKETS],
  inProgress: [NAV_CONFIG.GAME, NAV_CONFIG.BOX_SCORE, NAV_CONFIG.PLAY_BY_PLAY],
  team: [
    NAV_CONFIG.HOME,
    NAV_CONFIG.SCHEDULE,
    NAV_CONFIG.ROSTER,
    NAV_CONFIG.STATS,
    NAV_CONFIG.TEAM_TICKETS,
  ],
};

interface TeamNavProps {
  pageStatus: string;
  canonicalURL: string;
}

const createStyle = (hoverColor: string) => StyleSheet.create({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    padding: '10px 0',
    overflowX: 'auto',
    listStyle: 'none',
    [MEDIA_BREAKPOINTS.small]: {
      padding: '10px 15px',
    },
  },
  selected: {
    textDecoration: 'underline',
    textDecorationThickness: '3px',
    textUnderlineOffset: '12px',
    ':hover': {
      color: hoverColor,
    },
  },
  listItem: {
    marginRight: '15px',
  },
});

export const TeamNav = ({ pageStatus, canonicalURL }: TeamNavProps) => {
  const { hoverColor, linkColor } = useTheme<SportradarThemeProps>(
    componentConfigThemeKey,
  );
  const styles = createStyle(hoverColor);
  const linkProps = {
    activeStateColor: linkColor,
    hoverStateColor: hoverColor,
    idleColor: linkColor,
    currentLinkColor: linkColor,
    linkFontSizeLarge: TypographySizes.NORMAL,
    linkFontSizeMedium: TypographySizes.NORMAL,
    linkFontSizeSmall: TypographySizes.NORMAL,
  };
  const baseUrl = new URL(canonicalURL);
  const pathParts = baseUrl.pathname.split('/');
  const includesPath = pathParts.filter(href => PATHS.includes(href))[0];

  const buildLink = (href: string) => {
    if (includesPath && href === includesPath) {
      return baseUrl.href;
    }
    if (href.includes('tickets')) {
      if (pageStatus !== 'team') {
        return NAV_CONFIG.TICKETS.href;
      }

      return `${NAV_CONFIG.TEAM_TICKETS.href}/${pathParts[pathParts.length - 2]}`;
    }

    const pathIndex = canonicalURL.indexOf(includesPath);
    return `${canonicalURL.slice(0, pathIndex)}/${href}`;
  };

  const buildList = () => {
    return PAGE_LINKS[pageStatus].map((link: any, index: number) => {
      const { text, href } = link;
      const isActive = includesPath === href || (href === '' && !includesPath);
      const activeStyle = isActive ? styles.selected : null;
      const url = buildLink(href);

      return (
        <li
          key={`team-nav${index}`}
          className={css(styles.listItem, activeStyle)}
        >
          <RegularHeaderLink
            {...linkProps}
            href={url}
            text={text}
            isActive={isActive}
          />
        </li>
      );
    });
  };

  return (
    <nav>
      <ul className={css(styles.container)}>{buildList()}</ul>
    </nav>
  );
};
